import type { IconName } from "@openanalyst-ai/ui/icon"
import type { PermissionMode } from "@/context/permission-auto-respond"

export const REASONING_ORDER = ["none", "minimal", "low", "medium", "high", "extra_high", "xhigh", "max"] as const
export const FALLBACK_REASONING = ["low", "medium", "high", "extra_high"] as const
export const REASONING_LABELS: Record<string, string> = {
  none: "None",
  minimal: "Minimal",
  low: "Low",
  medium: "Medium",
  high: "High",
  extra_high: "Extra High",
  xhigh: "Extra High",
  max: "Max",
}

export const PERMISSION_MODE_OPTIONS = [
  { value: "default", label: "Default Ask", icon: "hand-stop", iconClass: "text-icon-base" },
  { value: "auto-review", label: "Auto-review", icon: "shield-terminal", iconClass: "text-icon-warning-active" },
  { value: "full-access", label: "Full access", icon: "circle-exclamation", iconClass: "text-icon-critical-base" },
] as const satisfies readonly {
  value: PermissionMode
  label: string
  icon: IconName
  iconClass: string
}[]

export const FULL_ACCESS_CONFIRMATION_COPY = {
  title: "Enable full access?",
  description:
    "When OpenAnalyst runs with full access, it can edit any file on your computer and run commands with network, without your approval.",
  warning:
    "Exercise caution when enabling full access. This significantly increases the risk of data loss, leaks, or unexpected behavior.",
  cancel: "Cancel",
  confirm: "Yes, continue anyway",
} as const

export function reasoningLabel(value: string | undefined) {
  if (!value) return REASONING_LABELS.medium
  return REASONING_LABELS[value] ?? value
}

export function reasoningTriggerLabel(value: string | undefined) {
  return reasoningLabel(value)
}

export function accountPlanLabel(value: string | undefined) {
  const label = value?.trim()
  if (!label) return "Account Plan"

  return label
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => {
      const normalized = word === word.toUpperCase() ? word.toLowerCase() : word
      return `${normalized.charAt(0).toUpperCase()}${normalized.slice(1)}`
    })
    .join(" ")
}

export function sortReasoning(values: readonly string[]) {
  const ordered = REASONING_ORDER.filter((item) => values.includes(item))
  const known = new Set<string>(ordered)
  return [...ordered, ...values.filter((item) => !known.has(item))]
}

export function permissionModeOption(value: PermissionMode) {
  return PERMISSION_MODE_OPTIONS.find((item) => item.value === value) ?? PERMISSION_MODE_OPTIONS[0]
}

export function shouldConfirmFullAccessPermission(current: PermissionMode, next: PermissionMode) {
  return next === "full-access" && current !== "full-access"
}

export function isPermissionMode(value: string): value is PermissionMode {
  return PERMISSION_MODE_OPTIONS.some((item) => item.value === value)
}
